import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { isRequestAborted, shouldReportApiError } from "@/api/api-utils.js";

/**
 * Utility function to merge class names with Tailwind CSS classes
 * Combines clsx and tailwind-merge for optimal class name handling
 * @param {...import('clsx').ClassValue} inputs - Class name inputs to merge
 * @returns {string} Merged class names
 */
export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Common error handling utilities
 */
export const errorUtils = {
  /**
   * Handle API errors consistently
   * @param {string} operation - Operation being performed
   * @param {Error} error - Error object
   * @param {Function} onError - Optional error callback
   */
  handleApiError: (operation, error, onError) => {
    if (isRequestAborted(error) || !shouldReportApiError(error)) return;
    const errorMessage = `Error ${operation}: ${error.message}`;
    console.error(errorMessage, error);

    if (onError && typeof onError === "function") {
      onError(error);
    }
  },
};

/**
 * Array utilities to reduce repeated operations
 */
export const arrayUtils = {
  /**
   * Check if array has items
   * @param {Array} array - Array to check
   * @returns {boolean} True if array has items
   */
  hasItems: (array) => Array.isArray(array) && array.length > 0,

  /**
   * Check if array is empty
   * @param {Array} array - Array to check
   * @returns {boolean} True if array is empty
   */
  isEmpty: (array) => !Array.isArray(array) || array.length === 0,

  /**
   * Get first item or default
   * @param {Array} array - Array to get first item from
   * @param {any} defaultValue - Default value if array is empty
   * @returns {any} First item or default
   */
  firstOrDefault: (array, defaultValue = null) =>
    Array.isArray(array) && array.length > 0 ? array[0] : defaultValue,

  /**
   * Get last item or default
   * @param {Array} array - Array to get last item from
   * @param {any} defaultValue - Default value if array is empty
   * @returns {any} Last item or default
   */
  lastOrDefault: (array, defaultValue = null) =>
    Array.isArray(array) && array.length > 0
      ? array[array.length - 1]
      : defaultValue,

  /**
   * Remove duplicates from array
   * @param {Array} array - Array to deduplicate
   * @param {Function} keySelector - Function to select key for comparison
   * @returns {Array} Deduplicated array
   */
  unique: (array, keySelector = (item) => item) => {
    const seen = new Set();
    return array.filter((item) => {
      const key = keySelector(item);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  },

  /**
   * Group array items by key
   * @param {Array} array - Array to group
   * @param {Function} keySelector - Function to select grouping key
   * @returns {Object} Grouped items
   */
  groupBy: (array, keySelector) => {
    return array.reduce((groups, item) => {
      const key = keySelector(item);
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
      return groups;
    }, {});
  },

  /**
   * Find item in array or return default
   * @param {Array} array - Array to search
   * @param {Function} predicate - Search predicate
   * @param {any} defaultValue - Default value if not found
   * @returns {any} Found item or default
   */
  findOrDefault: (array, predicate, defaultValue = null) => {
    const found = array.find(predicate);
    return found !== undefined ? found : defaultValue;
  },
};

/**
 * Math utilities to reduce repeated calculations
 */
export const mathUtils = {
  /**
   * Round to specified decimal places
   * @param {number} value - Value to round
   * @param {number} decimals - Number of decimal places
   * @returns {number} Rounded value
   */
  round: (value, decimals = 0) =>
    Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals),

  /**
   * Floor division
   * @param {number} dividend - Number to divide
   * @param {number} divisor - Divisor
   * @returns {number} Floored result
   */
  floorDiv: (dividend, divisor) => Math.floor(dividend / divisor),

  /**
   * Calculate percentage
   * @param {number} value - Value
   * @param {number} total - Total
   * @returns {number} Percentage
   */
  percentage: (value, total) => (total === 0 ? 0 : (value / total) * 100),

  /**
   * Get maximum value from array
   * @param {Array<number>} values - Array of numbers
   * @returns {number} Maximum value
   */
  max: (...values) => Math.max(...values),

  /**
   * Get minimum value from array
   * @param {Array<number>} values - Array of numbers
   * @returns {number} Minimum value
   */
  min: (...values) => Math.min(...values),

  /**
   * Ceiling function
   * @param {number} value - Value to round up
   * @returns {number} Ceiled value
   */
  ceil: (value) => Math.ceil(value),
};
